import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import Template from '../../components/template/template-component';
import HeaderComponent from '../../components/header/header-component';
import useBigbrain from '../../hooks/use-bigbrain';

import 'monday-ui-react-core/dist/main.css';

import './main-view.scss';

import Loader from 'monday-ui-react-core/dist/Loader';
import Button from 'monday-ui-react-core/dist/Button';
import ButtonGroup from 'monday-ui-react-core/dist/ButtonGroup';
import { mondayNotice } from '../../services/monday-service';
import { noticeTypes, bigBrainEvents } from '../../constants';
import SettingsComponent from '../../components/settings/settings-component';
import FeedbackComponent from '../../components/feedback/feedback-component';
import { useTipseens } from '../../contexts/tipseen-context';
import GuidenceComponent from '../../components/guidence/guidence-component';
import { GUIDENCE_TIPSEEN } from '../../constants/tipseens';
import { orderLinksAPI, ordersAPI } from '../../api';
import { useOrderContext } from '../../contexts/order-context';
import { useMondayContext } from '../../contexts/monday-context';
import { buildOrderToShare, openShareOrderModal, saveOrderToPDF } from '../../services/orders-service';
import { settingsAPI } from '../../api';
import useCanFeatures from '../../hooks/use-can-features';

function MainView() {
  const { hasBBLoaded, bigBrainTrack } = useBigbrain();
  const {
    sessionToken,
    mondayContext: { boardId },
  } = useMondayContext();
  const { isTipseenSynced, isTipseensLoaded } = useTipseens();
  const { order } = useOrderContext();
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const downloadableRef = useRef(null);
  const { canRemoveDocumentBranding } = useCanFeatures();

  useEffect(() => {
    if (hasBBLoaded && boardId) {
      bigBrainTrack(bigBrainEvents.GENERAL.APP_INIT, { info1: boardId });
    }
  }, [hasBBLoaded, bigBrainTrack, boardId]);

  const togglePreviewMode = useCallback(() => {
    bigBrainTrack(bigBrainEvents.HEADER.PREVIEW_CLICKED);
    setPreviewMode(!previewMode);
  }, [previewMode, setPreviewMode, bigBrainTrack]);

  const onDownloadClick = useCallback(async () => {
    bigBrainTrack(bigBrainEvents.HEADER.DOWNLOAD_CLICKED, { info1: boardId });
    setLoading(true);
    try {
      const { settings } = await settingsAPI.getSettings(sessionToken);
      const removeBrand = canRemoveDocumentBranding && !!settings?.removeBrand;
      await saveOrderToPDF(downloadableRef.current, removeBrand);
    } catch (e) {
      mondayNotice({ type: noticeTypes.ERROR, message: 'Error while trying to produce your quote, please try again' });
    }
    setLoading(false);
  }, [bigBrainTrack, downloadableRef, boardId, canRemoveDocumentBranding, sessionToken]);

  const onSendClick = useCallback(async () => {
    bigBrainTrack(bigBrainEvents.HEADER.SEND_CLICKED, { info1: boardId });
    try {
      let { orderLinkId, fileKey } = await ordersAPI.fetchLatestOrderLink({ orderId: order.id, sessionToken });
      openShareOrderModal(orderLinkId);
      if (!fileKey) {
        const { settings } = await settingsAPI.getSettings(sessionToken);
        const removeBrand = canRemoveDocumentBranding && !!settings?.removeBrand;
        const response = await orderLinksAPI.saveFile({
          orderLinkId,
          file: buildOrderToShare(downloadableRef.current, removeBrand),
          sessionToken,
        });

        if (!response.success) {
          throw new Error();
        }
      }
    } catch (e) {
      bigBrainTrack(bigBrainEvents.SHARE.SHARE_MODAL_FAILED);
      mondayNotice({ type: noticeTypes.ERROR, message: 'Sending is not available now' });
    }
  }, [order.id, bigBrainTrack, sessionToken, boardId, canRemoveDocumentBranding]);

  const headerActions = useMemo(() => {
    const rightActions = [
      {
        actionId: 'send',
        children: 'Send',
        component: Button,
        componentProps: {
          kind: Button.kinds.TERTIARY,
          onClick: onSendClick,
        },
      },
      {
        actionId: 'download-pdf',
        children: 'Download',
        component: Button,
        componentProps: {
          kind: Button.kinds.TERTIARY,
          onClick: onDownloadClick,
        },
      },
    ];

    const leftActions = [
      {
        actionId: 'settings',
        component: SettingsComponent,
        componentProps: {},
      },
      {
        actionId: 'feedback',
        component: FeedbackComponent,
        componentProps: {},
      },
    ];

    const EDIT_DOCUMENT = 'Edit';
    const VIEW_DOCUMENT = 'View';

    const middleActions = [
      {
        actionId: 'preview',
        component: ButtonGroup,
        componentProps: {
          options: [
            { value: EDIT_DOCUMENT, text: EDIT_DOCUMENT },
            { value: VIEW_DOCUMENT, text: VIEW_DOCUMENT },
          ],
          value: EDIT_DOCUMENT,
          onSelect: (value) => (value === EDIT_DOCUMENT ? setPreviewMode(false) : setPreviewMode(true)),
          size: ButtonGroup.sizes.SMALL,
        },
      },
    ];

    return { rightActions, leftActions, middleActions };
  }, [togglePreviewMode, onDownloadClick, previewMode, onSendClick]);

  const renderTemplate = () => (
    <div className="section-wrapper template">
      <div id="template-wrapper" className="template-wrapper section">
        <Template
          downloadableRef={downloadableRef}
          previewMode={previewMode}
          togglePreviewMode={togglePreviewMode}
          onDownloadClick={onDownloadClick}
        />
        {loading && (
          <div className="loading">
            <div className="wrapper">
              <Loader />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderGuidence = () => (
    <div className="section-wrapper guidence">
      <GuidenceComponent />
    </div>
  );

  const renderContent = () => {
    if (isTipseensLoaded) {
      if (isTipseenSynced(GUIDENCE_TIPSEEN)) {
        return (
          <>
            <div className="section-wrapper actions">
              <div className="header-template-actions-wrapper">
                <HeaderComponent headerActions={headerActions} />
              </div>
            </div>
            {renderTemplate()}
          </>
        );
      }

      return renderGuidence();
    }
    return (
      <div className="content-loader">
        <Loader />
      </div>
    );
  };

  return <div className="main-view">{renderContent()}</div>;
}

export default MainView;
